import React, { FC } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
// Components
import { Modal, Loader } from '../../../components';
import { Close, Save } from '@mui/icons-material';
import { Fade, Typography, Button, Grid, TextField, Box, Switch, FormControlLabel } from '@mui/material';
import { IDescription } from '../../../models';
import { updateOrderDescription, createOrderDescription } from '../../../fetch';

interface IOrderDescriptionModal {
  open: boolean;
  onClose: () => void;
  currentOrderDescription?: IDescription;
  fetchOrderDescriptions: () => {};
}

const OrderTypeSchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters').required('Required'),
  description: Yup.string().max(255, 'Max 255 characters'),
  isActive: Yup.boolean()
});

export const OrderDescriptionModal: FC<IOrderDescriptionModal> = ({ open, onClose, currentOrderDescription, fetchOrderDescriptions }) => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentOrderDescription && currentOrderDescription.name ? currentOrderDescription.name : '',
          description: currentOrderDescription && currentOrderDescription.additionalDetails ? currentOrderDescription.additionalDetails : '',
          isActive: currentOrderDescription && currentOrderDescription.isActive === false ? currentOrderDescription.isActive : true
        }}
        validationSchema={OrderTypeSchema}
        onSubmit={async (values, actions) => {
          try {
            let res = null;
            if (currentOrderDescription && currentOrderDescription.id) {
              res = await updateOrderDescription(currentOrderDescription.id, {
                ...values
              });
            } else {
              res = await createOrderDescription(values);
            }
            if (res && res.Status >= 400) {
              if (res && res.Errors && Object.values(res.Errors)[0] && Object.values(Object.values(res.Errors)[0])[0]) {
                enqueueSnackbar(Object.values(Object.values(res.Errors)[0])[0] as string, {
                  variant: 'error'
                });
              } else {
                enqueueSnackbar('Place Order Type with this name already exists.', {
                  variant: 'error'
                });
              }
            } else {
              enqueueSnackbar(currentOrderDescription ? 'Order Type Updated!' : 'Order Type Created!', {
                variant: 'success'
              });
              onClose();
              fetchOrderDescriptions();
              actions.resetForm();
            }
          } catch (error) {
            console.log(error);
            if (error && error.Errors && Object.values(error.Errors)[0] && Object.values(Object.values(error.Errors)[0])[0]) {
              enqueueSnackbar(Object.values(Object.values(error.Errors)[0])[0] as string, {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({ resetForm, isSubmitting, values, initialValues, setFieldValue, errors, touched, handleSubmit, dirty, isValid, handleBlur }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                }
              }}
              maxWidth="xs"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box marginBottom="1rem">
                    <Typography variant="h5">{currentOrderDescription ? `Edit Order Description` : 'Add Order Description'}</Typography>
                  </Box>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={values.isActive}
                            onChange={(_, checked) => {
                              setFieldValue('isActive', checked);
                            }}
                          />
                        }
                        label="Active"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Name"
                        name="name"
                        value={values.name}
                        onBlur={handleBlur}
                        size="small"
                        onChange={e => setFieldValue('name', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Description"
                        name="description"
                        value={values.description}
                        onBlur={handleBlur}
                        size="small"
                        multiline
                        rows={2}
                        inputProps={{
                          style: {
                            resize: 'vertical'
                          }
                        }}
                        onChange={e => setFieldValue('description', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Box marginTop="2rem">
                    <Button disabled={!dirty || isSubmitting || !isValid} type="submit" startIcon={<Save />} variant="contained" color="primary">
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
